import moment from 'moment';
import { FilteringRowType, GridFilterType, GridSortType, TableColumnType, TableRowType } from 'src/types/table';
import { FILTER_ARR_END_CHAR, FILTER_ARR_START_CHAR } from '../Table/constants';

export const getNumberToSort = (num: string | number | Date | null): number => {
    if (!num && num !== 0) {
        return -100000000;
    }
    return Number(num);
};

const NUMERIC_SORT_VALUE_REGEX = /^-?\d+(\.\d+)?$/;

export const parseNumericSortValue = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const str = String(value).trim();
    if (!NUMERIC_SORT_VALUE_REGEX.test(str)) {
        return null;
    }
    const num = Number(str);
    return Number.isFinite(num) ? num : null;
};

export const compareNumericOrAlphabetic = (aValue: unknown, bValue: unknown): number => {
    const numA = parseNumericSortValue(aValue);
    const numB = parseNumericSortValue(bValue);
    if (numA !== null && numB !== null) {
        return numA - numB;
    }
    return String(aValue ?? '').localeCompare(String(bValue ?? ''));
};

export const sortRows = (
    allRows: TableRowType[],
    sortObject: GridSortType,
    columns: TableColumnType[]
): TableRowType[] => {
    if (!allRows || !allRows.length) {
        return [];
    }
    const columnType = columns.find((column) => column.key === sortObject.column)?.type || 'string';
    let rows = [];

    if (sortObject.numericOrAlphabetic) {
        rows = allRows.concat().sort((a, b): number => {
            return compareNumericOrAlphabetic(a[sortObject.column], b[sortObject.column]);
        });
    } else if (sortObject.createIdNumber) {
        rows = allRows.concat().sort((a, b): number => {
            const numA = getNumberToSort(Number(a[sortObject.column]));
            const numB = getNumberToSort(Number(b[sortObject.column]));
            if (numA >= 0 && numB < 0) {
                return -1;
            } else if (numA < 0 && numB >= 0) {
                return 1;
            } else {
                return numA - numB;
            }
        });
    } else {
        switch (columnType) {
            case 'date':
            case 'dateTime':
                rows = allRows.concat().sort((a, b): number => {
                    return (
                        new Date(a[sortObject.column] || 0).getTime() - new Date(b[sortObject.column] || 0).getTime()
                    );
                });
                break;

            case 'number':
            case 'number-filter':
                rows = allRows.concat().sort((a, b): number => {
                    return (
                        getNumberToSort(Number(a[sortObject.column])) - getNumberToSort(Number(b[sortObject.column]))
                    );
                });
                break;

            case 'string':
            default:
                rows = allRows.concat().sort((a, b): number => {
                    // flow need typecheck
                    if (typeof a[sortObject.column] === 'object' && typeof b[sortObject.column] === 'object') {
                        return 0;
                    }
                    if (typeof a[sortObject.column] === 'object') {
                        return -1;
                    }
                    if (typeof b[sortObject.column] === 'object') {
                        return 1;
                    }
                    return `${a[sortObject.column]}`.localeCompare(`${b[sortObject.column]}`);
                });
                break;
        }
    }

    return sortObject.direction === 'ASC' ? rows : rows.reverse();
};

export const filterRows = (
    allRows: TableRowType[],
    filterObject: GridFilterType[],
    columns: TableColumnType[],
    customFilter?: FilteringRowType
) => {
    return allRows.filter((row): boolean => {
        return customFilter ? customFilter(row, filterObject, columns) : filteringRow(row, filterObject, columns);
    });
};

type FceCheckType = (rowValue: string, strFilter: string) => boolean;

const ifArray = (rowValue: string, filterValue: string, fceCheck: FceCheckType): boolean => {
    let isOk = true;
    if (filterValue[0] === FILTER_ARR_START_CHAR && filterValue[filterValue.length - 1] === FILTER_ARR_END_CHAR) {
        const arrFilter = filterValue.replace(FILTER_ARR_START_CHAR, '').replace(FILTER_ARR_END_CHAR, '').split(',');

        if (!arrFilter.filter((strFilter) => fceCheck(rowValue, strFilter)).length) {
            isOk = false;
        }
    } else if (!fceCheck(rowValue, filterValue)) {
        isOk = false;
    }

    return isOk;
};

const checkString = (rowValue: string, filterValue: string): boolean => {
    if (!rowValue) {
        return false;
    }
    const stringRowVal = String(rowValue).toLocaleLowerCase();
    const trimValue = filterValue.trim().toLocaleLowerCase();
    const normalizedRowVal = stringRowVal
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

    return (
        stringRowVal.includes(trimValue) ||
        normalizedRowVal.includes(trimValue)
    );
};

export const filteringRow: FilteringRowType = (row, filterObject, columns) => {
    let isOk = true;
    filterObject.forEach((filterObjectItem) => {
        const filterColumn = columns.find((column) => column.key === filterObjectItem.column);
        const columnType = filterColumn?.type || 'string';
        const filterValue = filterObjectItem.value;
        const filterOperator = filterObjectItem.operator;
        const rowValue = filterColumn?.selectOptionKeyFce
            ? filterColumn.selectOptionKeyFce(row)
            : row[filterObjectItem.column];

        if (isOk && filterValue) {
            switch (columnType) {
                case 'date':
                    if (filterValue && filterOperator) {
                        switch (filterOperator) {
                            case 'between':
                                if (Array.isArray(filterValue) && filterValue[0] && filterValue[1]) {
                                    if (!moment(rowValue).isBetween(moment(filterValue[0]), moment(filterValue[1]))) {
                                        isOk = false;
                                    }
                                }
                                break;
                            //         case 'eq':
                            //             if (moment.isMoment(filterValue)) {
                            //                 if (filterValue.format('l') !== moment(rowValue).format('l')) {
                            //                     isOk = false;
                            //                 }
                            //             }
                            //             break;
                            //         case 'lt':
                            //             if (moment.isMoment(filterValue)) {
                            //                 if (filterValue < moment(rowValue)) {
                            //                     isOk = false;
                            //                 }
                            //             }
                            //             break;
                            //         case 'gt':
                            default:
                                //             if (moment.isMoment(filterValue)) {
                                //                 if (filterValue > moment(rowValue)) {
                                //                     isOk = false;
                                //                 }
                                //             }
                                break;
                        }
                    }
                    break;

                case 'multi-select':
                    if (Array.isArray(filterValue)) {
                        if (
                            !filterValue.filter(
                                (strFilter: string): boolean => rowValue && rowValue.includes(strFilter)
                            ).length
                        ) {
                            isOk = false;
                        }
                    }
                    break;
                case 'select':
                    if (Array.isArray(filterValue)) {
                        if (
                            !filterValue.filter((strFilter: string): boolean => strFilter === String(rowValue)).length
                        ) {
                            isOk = false;
                        }
                    }
                    break;

                case 'number-filter':
                    if (!Array.isArray(filterValue)) {
                        if (filterValue && filterOperator) {
                            switch (filterOperator) {
                                case 'lt':
                                    if (Number(rowValue) >= Number(filterValue)) {
                                        isOk = false;
                                    }
                                    break;
                                case 'gt':
                                    if (Number(rowValue) <= Number(filterValue)) {
                                        isOk = false;
                                    }
                                    break;
                                case 'lte':
                                    if (Number(rowValue) > Number(filterValue)) {
                                        isOk = false;
                                    }
                                    break;
                                case 'gte':
                                    if (Number(rowValue) < Number(filterValue)) {
                                        isOk = false;
                                    }
                                    break;
                                case 'eq':
                                    if (
                                        !ifArray(
                                            String(rowValue),
                                            filterValue,
                                            (value, filter) => Number(value) === Number(filter)
                                        )
                                    ) {
                                        isOk = false;
                                    }
                                    break;
                                case 'like':
                                default:
                                    if (
                                        !ifArray(
                                            String(rowValue),
                                            filterValue,
                                            (value, filter) => value.indexOf(filter) > -1
                                        )
                                    ) {
                                        isOk = false;
                                    }
                                    break;
                            }
                        }
                    }
                    break;

                case 'number':
                    if (!Array.isArray(filterValue)) {
                        if (!ifArray(`${rowValue}`, filterValue, (value, filter) => value.indexOf(filter) > -1)) {
                            isOk = false;
                        }
                    } else {
                        if (
                            !filterValue.filter((strFilter: string): boolean => String(rowValue).includes(strFilter))
                                .length
                        ) {
                            isOk = false;
                        }
                    }
                    break;

                default:
                    if (!Array.isArray(filterValue)) {
                        if (typeof rowValue === 'object') {
                            isOk = false;
                        } else if (
                            !ifArray(String(rowValue), filterValue, (value, filter) => checkString(value, filter))
                        ) {
                            isOk = false;
                        }
                    } else {
                        if (
                            !filterValue.filter((strFilter: string): boolean => String(rowValue).includes(strFilter))
                                .length
                        ) {
                            isOk = false;
                        }
                    }
                    break;
            }
        }
    });

    return isOk;
};
